import { uniqBy } from 'lodash';

import { createReducer } from 'typesafe-actions';

import {
  TCardRestrictionsGroups,
  IProfile,
  IProfileCard,
  IProfileKYC,
  IProfileKYCsState,
} from '@/store/models/profile.model';
import { IState } from '@/store/types/search.types';
import { ITaxResidence } from '@/store/models/taxResidence.model';
import { IProfileState, TProfileAction, ProfileActionTypes } from '@/store/types/profile.types';
import {
  ISelectorsTaxResidence,
  TTaxResidenceGroupAction,
  taxResidenceTypes,
} from '@/features/taxResidences/store/tax-residence.types';
import { profileKycReducer } from '@/features/kyc/store/reducers/profile-kyc.reducer';
import { companyMemberReducer } from '@/features/company-members/store/reducers/company-members.reducer';
import { userReducer } from '@/features/user/store/user.reducer';
import { walletReducer } from '@/features/wallets/store/reducers/wallet.reducer';
import { IHandleAsynProfileActions, IHandleAsynProfileSelectors } from '@/features/user/store/profile.types';
import { ISelectorsSCA } from '@/features/2fa/store/types/auth2Fa.types';

export const initialState: IProfileState = {
  profiles: {
    state: [],
  },
};

export const handleAsyncProfileReducer = (
  currentState: IProfileState,
  userId: number,
  selectors: IHandleAsynProfileSelectors,
  actions?: IHandleAsynProfileActions,
): IProfileState => {
  const profilesState = currentState.profiles.state?.map((profileState): IState<IProfile> => {
    if (profileState.state?.user.state?.userId === userId) {
      if (selectors.cards) {
        if (selectors.cards.cardId) {
          if (selectors.cards.restrictionsGroupType) {
            return handleCardRestrictionsGroup(selectors, profileState, actions);
          }

          if (selectors.cards.option) {
            return handleCardOption(selectors, profileState, actions);
          }

          if (selectors.cards.cardTransactions) {
            return handleCardTransactions(profileState, selectors, actions);
          }

          if (selectors.cards.action) {
            return handleCardAction(selectors, profileState, actions);
          }

          if (selectors.cards.digitizedCards) {
            return handleDigitizedCards(profileState, selectors, actions);
          }

          if (selectors.cards.enrollments) {
            return handleEnrollments(profileState, selectors, actions);
          }

          if (selectors.cards.simulation) {
            return handleTransactionSimulation(profileState, selectors);
          }

          return handleCard(profileState, selectors, actions);
        }

        return handleCards(profileState, actions);
      }

      if (selectors.children) {
        if (selectors.children.create) {
          return handleCreateChild(profileState, actions);
        }

        if (selectors.children.items?.id) {
          if (selectors.children.items.taxResidences) {
            return handleCreateChildTaxResidence(profileState, selectors.children.items?.id, actions);
          }
          if (selectors.children.items.kyc) {
            return handleCreateChildKYC(profileState, selectors.children.items?.id, actions);
          }
        }

        return handleChildren(profileState, actions);
      }

      if (selectors.beneficiaries) {
        if (selectors.beneficiaries.create) {
          return handleCreateBeneficiary(profileState, actions);
        }

        return handleBeneficiaries(profileState, actions);
      }

      if (selectors.kyc) {
        if (selectors.kyc.payins) {
          return handleSubmitKYCPayins(profileState, actions);
        }

        if (selectors.kyc.submit) {
          return handleSubmitKYCDocuments(profileState, actions);
        }

        if (selectors.kyc.create) {
          return handleCreateKYCDocument(profileState, actions);
        }

        if (selectors.kyc.documentId) {
          return handleKYCDocument(profileState, selectors, actions);
        }

        return handleKYCDocuments(profileState, actions);
      }

      if (selectors.user) {
        return handleUser(profileState, actions);
      }

      if (selectors.forgotPassword) {
        return handleForgotPassword(profileState, actions);
      }

      if (selectors.deactivate) {
        return handleDeactivate(profileState, actions);
      }

      if (selectors.sca) {
        if (selectors.sca.wallets) {
          return handleSCAWallet(profileState, actions);
        }

        if (selectors.sca.scaWalletId) {
          return handleSCAWalletActionById(profileState, actions, selectors.sca.scaWalletId);
        }

        if (selectors.sca.deletedWallets) {
          return handleDeletedSCAWallets(profileState, actions);
        }
      }

      if (selectors.taxResidences) {
        if (selectors.taxResidences.items) return handleTaxResidence(profileState, actions);

        if (selectors.taxResidences.id) {
          return handleTaxResidenceActionById(profileState, actions, selectors.taxResidences.id);
        }

        if (selectors.taxResidences.create) {
          return handleCreateTaxResidence(profileState, actions);
        }
      }

      return handleProfile(profileState, actions);
    }
    return profileState;
  });

  return {
    ...currentState,
    profiles: {
      ...currentState.profiles,
      state: profilesState,
    },
  };
};

function handleProfile(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  return {
    ...profileState,
    isLoading: !profileState.isLoading,
    ...action,
  };
}

function handleDeactivate(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      user: {
        ...profileState.state.user,
        state: action?.state || profileState.state.user.state,
      },
      deactivate: {
        ...profileState.state?.deactivate,
        isLoading: !profileState.state?.deactivate?.isLoading,
        error: action?.error,
      },
    },
  };
}

function handleUser(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      user: {
        ...profileState.state?.user,
        isLoading: !profileState.state?.user.isLoading,
        ...action,
      },
    },
  };
}

function handleForgotPassword(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      forgotPassword: {
        ...profileState.state?.forgotPassword,
        isLoading: !profileState.state?.forgotPassword?.isLoading,
        ...action,
      },
    },
  };
}

function handleKYCDocuments(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  const isLoading = !(action?.error || action?.state);

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileKYCs: {
        ...profileState.state?.profileKYCs,
        state: { ...profileState.state.profileKYCs?.state, ...action?.state },
        error: action?.error,
        isLoading,
      },
    },
  };
}

function handleKYCDocument(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileKYCs: {
        ...profileState.state.profileKYCs,
        state: {
          ...(profileState.state.profileKYCs?.state || ({} as IProfileKYCsState)),
          documents: {
            ...profileState.state.profileKYCs?.state?.documents,
            isLoading: !profileState.state.profileKYCs?.state?.documents?.isLoading,
            error: action?.error || undefined,
            state: profileState.state.profileKYCs?.state?.documents?.state?.map((kycDocument) => {
              if (kycDocument.document.documentId === selectors.kyc?.documentId) {
                return {
                  ...(kycDocument || ({} as IProfileKYC)),
                  document: action?.state?.document || kycDocument.document,
                };
              }
              return kycDocument;
            }),
          },
        },
      },
    },
  };
}

function handleSubmitKYCPayins(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileKYCs: {
        ...profileState.state.profileKYCs,
        state: {
          ...(profileState.state.profileKYCs?.state || ({} as IProfileKYCsState)),
          payins: {
            isLoading: !profileState.state.profileKYCs?.state?.payins?.isLoading,
            ...action,
          },
        },
      },
    },
  };
}

function handleSubmitKYCDocuments(
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileKYCs: {
        ...profileState.state.profileKYCs,
        state: {
          ...(profileState.state.profileKYCs?.state || ({} as IProfileKYCsState)),
          documents: {
            ...profileState.state.profileKYCs?.state?.documents,
            submit: {
              isLoading: !profileState.state.profileKYCs?.state?.documents?.submit?.isLoading,
              ...action,
            },
          },
        },
      },
    },
  };
}

function handleCreateKYCDocument(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileKYCs: {
        ...profileState.state.profileKYCs,
        state: {
          ...(profileState.state.profileKYCs?.state || ({} as IProfileKYCsState)),
          documents: {
            ...profileState.state.profileKYCs?.state?.documents,
            create: {
              isLoading: action?.error ? false : true,
              error: action?.error,
              state: action?.state?.batchIndex
                ? {
                    ...(profileState.state.profileKYCs?.state?.documents?.create?.state || {}),
                    [action?.state?.batchIndex]: { isLoading: action?.error ? false : true, error: action?.error },
                  }
                : undefined,
            },
          },
        },
      },
    },
  };
}

function handleBeneficiaries(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      beneficiaries: {
        ...profileState.state.beneficiaries,
        isLoading: !profileState.state?.beneficiaries?.isLoading,
        state:
          action?.state?.map((beneficiary) => ({
            state: { ...beneficiary },
          })) || profileState.state.beneficiaries?.state,
        error: action?.error,
      },
    },
  };
}

function handleCreateBeneficiary(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      beneficiaries: {
        ...profileState.state?.beneficiaries,
        create: {
          isLoading: !profileState.state?.beneficiaries?.create?.isLoading,
          ...action,
        },
      },
    },
  };
}

function handleCreateChild(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;
  return {
    ...profileState,
    state: {
      ...profileState.state,
      children: {
        ...profileState.state?.children,
        create: {
          isLoading: !profileState.state?.children?.create?.isLoading,
          ...action,
        },
      },
    },
  };
}

function handleChildren(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      children: {
        state:
          action?.state?.children?.map((child) => ({
            state: { user: { state: child } },
          })) || profileState.state.children?.state,
        maxItems: action?.state?.maxItems ?? profileState.state.children?.maxItems,
        isLoading: !profileState.state?.children?.isLoading,
        error: action?.error,
      },
    },
  };
}

function handleCards(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        isLoading: !profileState.state?.profileCards?.isLoading,
        ...action,
      },
    },
  };
}

function handleCard(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              isLoading: !profileCard.isLoading,
              error: action?.error,
              state: {
                ...profileCard.state,
                card: action?.state || profileCard.state?.card,
              },
            };
          }

          return profileCard;
        }),
      },
    },
  };
}

function handleCardAction(
  selectors: IHandleAsynProfileSelectors,
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state || !selectors.cards?.action) return profileState;
  const selectedAction = selectors.cards?.action;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              state: {
                ...profileCard.state,
                card: action?.state || profileCard.state?.card,
                actions: {
                  ...profileCard.state?.actions,
                  [selectedAction]: {
                    ...profileCard.state.actions?.[selectedAction],
                    isLoading: !profileCard.state?.actions?.[selectedAction]?.isLoading,
                    ...action,
                  },
                },
              },
            };
          }
          return profileCard;
        }),
      },
    },
  };
}

function handleCardOption(
  selectors: IHandleAsynProfileSelectors,
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              state: {
                ...profileCard.state,
                options: {
                  state: { ...(profileCard.state?.options?.state || {}) },
                  isLoading: !profileCard.state?.options?.isLoading,
                  error: action?.error,
                },
              },
            };
          }
          return profileCard;
        }),
      },
    },
  };
}

function handleCardRestrictionsGroup(
  selectors: IHandleAsynProfileSelectors,
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state || !selectors.cards?.restrictionsGroupType) return profileState;
  const type = selectors.cards.restrictionsGroupType;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard): IState<IProfileCard> => {
          if (profileCard.state && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              state: {
                ...profileCard.state,
                card: action?.state?.card || profileCard.state.card,
                restrictionsGroups: {
                  ...(profileCard.state?.restrictionsGroups || ({} as TCardRestrictionsGroups)),
                  [type]: action?.state?.deletedRestrictionsGroup
                    ? undefined
                    : {
                        group: action?.state?.restrictionsGroup || profileCard.state?.restrictionsGroups?.[type]?.group,
                        isLoading: !profileCard.state?.restrictionsGroups?.[type]?.isLoading,
                        error: action?.error,
                      },
                },
              },
            };
          }

          return profileCard;
        }),
      },
    },
  };
}

function handleCardTransactions(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state?.card && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              state: {
                ...profileCard.state,
                paginateCardTransactions: {
                  ...profileCard.state?.paginateCardTransactions,
                  isLoading: !profileCard.state?.paginateCardTransactions?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileCard;
        }),
      },
    },
  };
}

function handleDigitizedCards(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state?.card && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              state: {
                ...profileCard.state,
                digitizedCards: {
                  ...profileCard.state?.digitizedCards,
                  isLoading: !profileCard.state?.digitizedCards?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileCard;
        }),
      },
    },
  };
}

function handleEnrollments(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state?.card && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              state: {
                ...profileCard.state,
                enrollments: {
                  ...profileCard.state?.enrollments,
                  isLoading: !profileCard.state?.enrollments?.isLoading,
                  ...action,
                },
              },
            };
          }

          return profileCard;
        }),
      },
    },
  };
}

function handleTransactionSimulation(
  profileState: IState<IProfile>,
  selectors: IHandleAsynProfileSelectors,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      profileCards: {
        ...profileState.state?.profileCards,
        state: profileState.state?.profileCards?.state?.map((profileCard) => {
          if (profileCard.state?.card && profileCard.state?.card.cardId === selectors.cards?.cardId) {
            return {
              ...profileCard,
              state: {
                ...profileCard.state,
                simulation: {
                  transaction: {
                    ...profileCard.state?.simulation?.transaction,
                    isLoading: !profileCard.state?.simulation?.transaction?.isLoading,
                  },
                },
              },
            };
          }

          return profileCard;
        }),
      },
    },
  };
}

function handleSCAWallet(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      scaWallets: {
        state:
          action?.state?.map((sca) => ({
            state: sca,
          })) || profileState.state.scaWallets?.state,
        isLoading: !profileState.state?.scaWallets?.isLoading,
        error: action?.error,
      },
    },
  };
}

function handleDeletedSCAWallets(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      deletedScaWallets: {
        state: action?.state || profileState.state.deletedScaWallets?.state,
        isLoading: !profileState.state.deletedScaWallets?.isLoading,
        error: action?.error,
      },
    },
  };
}

function handleSCAWalletActionById(
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
  scaWalletId?: ISelectorsSCA['scaWalletId'],
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      scaWallets: {
        state:
          profileState.state.scaWallets?.state?.map((scaState) => {
            if (scaState.state?.scaWalletId === scaWalletId) {
              return {
                ...scaState,
                state: {
                  ...scaState.state,
                  ...action?.state,
                },
                isLoading: !scaState.isLoading,
                error: action?.error,
              };
            }

            return scaState;
          }) || [],
      },
    },
  };
}

function handleTaxResidence(profileState: IState<IProfile>, action?: IHandleAsynProfileActions): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      taxResidences: {
        state:
          action?.state?.map((taxResidence: ITaxResidence) => ({
            state: taxResidence,
          })) || profileState.state.taxResidences?.state,
        isLoading: !profileState.state?.taxResidences?.isLoading,
        error: action?.error,
      },
    },
  };
}

function handleCreateTaxResidence(
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;
  const { taxResidence, ...batchProps } = action?.state || {};

  return {
    ...profileState,
    state: {
      ...profileState.state,
      taxResidences: {
        ...profileState.state?.taxResidences,
        state: [
          ...(profileState.state?.taxResidences?.state || []),
          ...(taxResidence ? [{ state: taxResidence }] : []),
        ],
        create: {
          isLoading: !profileState.state?.taxResidences?.create?.isLoading,
          error: action?.error,
          state:
            Object.keys(batchProps).length > 0
              ? {
                  ...(profileState.state?.taxResidences?.create?.state || {}),
                  ...batchProps,
                }
              : undefined,
        },
      },
    },
  };
}

function handleTaxResidenceActionById(
  profileState: IState<IProfile>,
  action?: IHandleAsynProfileActions,
  taxResidenceId?: ISelectorsTaxResidence['id'],
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      taxResidences: {
        state:
          profileState.state.taxResidences?.state?.map((taxResidence) => {
            if (taxResidence.state?.id === taxResidenceId) {
              return {
                ...taxResidence,
                state: {
                  ...taxResidence.state,
                  ...action?.state,
                },
                isLoading: !taxResidence.isLoading,
                error: action?.error,
              };
            }

            return taxResidence;
          }) || [],
      },
    },
  };
}

function handleCreateChildTaxResidence(
  profileState: IState<IProfile>,
  childId: number,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;
  const { taxResidence, ...batchProps } = action?.state || {};

  return {
    ...profileState,
    state: {
      ...profileState.state,
      children: {
        ...profileState.state.children,
        state: profileState.state.children.state.map((childState: IState<Omit<IProfile, 'children'>>) => {
          if (childState.state?.user.state?.userId === childId) {
            return {
              ...childState,
              state: {
                ...childState.state,
                taxResidences: {
                  state: [
                    ...(childState.state?.taxResidences?.state || []),
                    ...(taxResidence ? [{ state: taxResidence }] : []),
                  ],
                  create: {
                    isLoading: !profileState.state?.taxResidences?.create?.isLoading,
                    error: action?.error,
                    state:
                      Object.keys(batchProps).length > 0
                        ? {
                            ...(childState.state?.taxResidences?.create?.state || {}),
                            ...batchProps,
                          }
                        : undefined,
                  },
                },
              },
            };
          }
          return childState;
        }),
      },
    },
  };
}

function handleCreateChildKYC(
  profileState: IState<IProfile>,
  childId: number,
  action?: IHandleAsynProfileActions,
): IState<IProfile> {
  if (!profileState.state) return profileState;

  return {
    ...profileState,
    state: {
      ...profileState.state,
      children: {
        ...profileState.state.children,
        state: profileState.state.children.state.map((childState: IState<Omit<IProfile, 'children'>>) => {
          if (childState.state?.user.state?.userId === childId) {
            return {
              ...childState,
              state: {
                ...childState.state,
                profileKYCs: {
                  state: {
                    ...(childState.state.profileKYCs?.state || ({} as IProfileKYCsState)),
                    documents: {
                      ...childState.state.profileKYCs?.state?.documents,
                      create: {
                        isLoading: action?.error ? false : true,
                        error: action?.error,
                        state: action?.state?.batchIndex
                          ? {
                              ...(childState.state.profileKYCs?.state?.documents?.create?.state || {}),
                              [action?.state?.batchIndex]: {
                                isLoading: action?.error ? false : true,
                                error: action?.error,
                              },
                            }
                          : undefined,
                      },
                    },
                  },
                },
              },
            };
          }
          return childState;
        }),
      },
    },
  };
}

const baseProfileReducer = createReducer<IProfileState, TProfileAction | TTaxResidenceGroupAction>(initialState)
  /**
   * Add profile reducers
   */
  .handleType(ProfileActionTypes.ADD_PROFILE, (state) => {
    return {
      ...state,
      profiles: {
        ...state.profiles,
        isLoading: true,
        error: undefined,
      },
    };
  })

  .handleType(ProfileActionTypes.ADD_PROFILE_FAIL, (state, action) => {
    return {
      ...state,
      profiles: {
        ...state.profiles,
        isLoading: false,
        error: action.error,
      },
    };
  })

  .handleType(ProfileActionTypes.ADD_PROFILE_SUCCESS, (state, action) => {
    const profiles = uniqBy(
      [...(state.profiles.state?.map((profileState) => profileState.state) || []), action.profile],
      'user.state.userId',
    );

    return {
      ...state,
      profiles: {
        ...state.profiles,
        state: profiles.map((profile) => ({
          state: profile,
          error: undefined,
        })),
        isLoading: false,
        error: undefined,
      },
    };
  })

  /**
   * Create child user reducers
   */
  .handleType(ProfileActionTypes.CREATE_CHILD_USER, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { children: { create: {} } },
      {
        state: action?.batchIndex
          ? {
              [action.batchIndex]: { isLoading: true },
            }
          : undefined,
      },
    ),
  )

  .handleType(ProfileActionTypes.CREATE_CHILD_USER_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { children: { create: {} } },
      {
        error: action.error,
        state: action?.batchIndex
          ? {
              [action.batchIndex]: { isLoading: false, error: action.error },
            }
          : undefined,
      },
    ),
  )

  .handleType(ProfileActionTypes.CREATE_CHILD_USER_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.user.parentUserId) {
        return {
          ...profile,
          state: {
            ...(profile.state as IProfile),
            children: {
              ...profile.state?.children,
              state: [...(profile.state?.children?.state || []), { state: { user: { state: action.user } } }],
              create: {
                isLoading: false,
                state: {
                  ...(action?.batchIndex
                    ? {
                        [action.batchIndex]: { isLoading: false, error: undefined },
                      }
                    : undefined),
                },
              },
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Remove profile reducer
   */
  .handleType(ProfileActionTypes.REMOVE_PROFILE, (state, action) => ({
    ...state,
    profiles: {
      state: state.profiles.state?.filter((profile) => profile.state?.user.state?.userId !== action.userId),
    },
  }))

  /**
   * Remove all profile reducer
   */
  .handleType(ProfileActionTypes.REMOVE_ALL_PROFILE, (state) => ({
    ...state,
    profiles: {
      state: [],
    },
  }))

  /**
   * Set Current Card reducer
   */
  .handleType(ProfileActionTypes.SET_CURRENT_CARD, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              currentCardId: action.currentCardId,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * unset Cards reducer
   */
  .handleType(ProfileActionTypes.UNSET_CARDS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              currentCardId: undefined,
              state: [],
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Simulate card transactions reducers
   */

  .handleType(ProfileActionTypes.SIMULATE_TRANSACTION, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, simulation: { transaction: {} } },
    }),
  )

  .handleType(ProfileActionTypes.SIMULATE_TRANSACTION_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, simulation: { transaction: {} } },
    }),
  )
  .handleType(ProfileActionTypes.SIMULATE_TRANSACTION_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, simulation: { transaction: {} } },
    }),
  )

  /**
   * Get mandataries reducers
   */
  .handleType(ProfileActionTypes.GET_MANDATARIES, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { mandataries: {} }),
  )

  .handleType(ProfileActionTypes.GET_MANDATARIES_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { mandataries: {} }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.GET_MANDATARIES_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { mandataries: {} },
      { state: action.mandataries, error: undefined },
    ),
  )

  /**
   * Get beneficiaries reducers
   */
  .handleType(ProfileActionTypes.GET_BENEFICIARIES, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { beneficiaries: {} }),
  )

  .handleType(ProfileActionTypes.GET_BENEFICIARIES_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { beneficiaries: {} }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.GET_BENEFICIARIES_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state.beneficiaries,
              isLoading: false,
              error: undefined,
              state: [...action.beneficiaries.map((beneficiary) => ({ state: { beneficiary } }))],
              meta: {
                ...action.meta,
              },
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Create beneficiary reducers
   */
  .handleType(ProfileActionTypes.CREATE_BENEFICIARY, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { beneficiaries: { create: {} } }),
  )

  .handleType(ProfileActionTypes.CREATE_BENEFICIARY_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { beneficiaries: { create: {} } }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.CREATE_BENEFICIARY_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state.beneficiaries,
              state: [...(profile.state.beneficiaries?.state || []), { state: { beneficiary: action.beneficiary } }],
              create: {
                isLoading: false,
              },
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Edit Beneficiary reducers
   */
  .handleType(ProfileActionTypes.EDIT_BENEFICIARY, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state?.beneficiaries,
              state:
                profile.state?.beneficiaries?.state?.map((beneficiaryState) => {
                  if (beneficiaryState.state?.beneficiary.id === action.beneficiary.id) {
                    return {
                      ...beneficiaryState,
                      isLoading: true,
                    };
                  }

                  return beneficiaryState;
                }) || [],
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })
  .handleType(ProfileActionTypes.EDIT_BENEFICIARY_FAIL, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state?.beneficiaries,
              state:
                profile.state?.beneficiaries?.state?.map((beneficiaryState) => {
                  if (beneficiaryState.state?.beneficiary.id === action.beneficiary.id) {
                    return {
                      ...beneficiaryState,
                      isLoading: false,
                      error: action.error,
                    };
                  }

                  return beneficiaryState;
                }) || [],
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(ProfileActionTypes.EDIT_BENEFICIARY_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state?.beneficiaries,
              state: profile.state?.beneficiaries?.state?.map((beneficiaryState) => {
                if (beneficiaryState.state?.beneficiary.id === action.beneficiary.id) {
                  return {
                    ...beneficiaryState,
                    isLoading: false,
                    state: { ...beneficiaryState.state, beneficiary: action.beneficiary },
                  };
                }

                return beneficiaryState;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Update Beneficiary status reducers
   */
  .handleType(ProfileActionTypes.UPDATE_BENEFICIARY_STATUS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state?.beneficiaries,
              state: profile.state?.beneficiaries?.state?.map((beneficiaryState) => {
                if (beneficiaryState.state?.beneficiary.id === action.beneficiary.id) {
                  return {
                    ...beneficiaryState,
                    state: {
                      ...beneficiaryState.state,
                      status: {
                        ...beneficiaryState.state.status,
                        isLoading: true,
                      },
                    },
                  };
                }
                return beneficiaryState;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })
  .handleType(ProfileActionTypes.UPDATE_BENEFICIARY_STATUS_FAIL, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state?.beneficiaries,
              state: profile.state?.beneficiaries?.state?.map((beneficiaryState) => {
                if (beneficiaryState.state?.beneficiary.id === action.beneficiary.id) {
                  return {
                    ...beneficiaryState,
                    state: {
                      ...beneficiaryState.state,
                      status: {
                        ...beneficiaryState.state.status,
                        isLoading: false,
                        error: action.error,
                      },
                    },
                  };
                }
                return beneficiaryState;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(ProfileActionTypes.UPDATE_BENEFICIARY_STATUS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.beneficiary.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            beneficiaries: {
              ...profile.state?.beneficiaries,
              state: profile.state?.beneficiaries?.state?.map((beneficiaryState) => {
                if (beneficiaryState.state?.beneficiary.id === action.beneficiary.id) {
                  return {
                    ...beneficiaryState,
                    state: {
                      ...beneficiaryState.state,
                      beneficiary: action.beneficiary,
                      status: { isLoading: false },
                    },
                  };
                }
                return beneficiaryState;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Search Cards reducers
   */
  .handleType(ProfileActionTypes.GET_CARDS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: {} }),
  )

  .handleType(ProfileActionTypes.GET_CARDS_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: {} }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.GET_CARDS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state.profileCards,
              state: action.cards.map((card) => ({ state: { card } })),
              isLoading: false,
              error: undefined,
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Get Current Card Transactions reducers
   */
  .handleType(ProfileActionTypes.GET_CURRENT_CARD_TRANSACTIONS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, cardTransactions: {} } }),
  )

  .handleType(ProfileActionTypes.GET_CURRENT_CARD_TRANSACTIONS_FAIL, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      paginateCardTransactions: {
                        isLoading: false,
                        error: action.error,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(ProfileActionTypes.GET_CURRENT_CARD_TRANSACTIONS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      paginateCardTransactions: {
                        isLoading: false,
                        state: { ...action.paginateCardTransactions },
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Lock or Unlock Card reducers
   */
  .handleType(ProfileActionTypes.BLOCK_CARD, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, action: action.blockAction } }),
  )

  .handleType(ProfileActionTypes.BLOCK_CARD_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, action: action.blockAction } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.BLOCK_CARD_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      {
        cards: { cardId: action.card.cardId, action: action.blockAction },
      },
      { state: action.card, error: undefined },
    ),
  )

  /**
   * Update Card reducers
   */
  .handleType(ProfileActionTypes.UPDATE_PAYMENT_CARD_LIMITS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    state: {
                      ...profileCard.state,
                      totalLimitCard: {
                        isLoading: true,
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(ProfileActionTypes.UPDATE_PAYMENT_CARD_LIMITS_FAIL, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    state: {
                      ...profileCard.state,
                      totalLimitCard: {
                        isLoading: false,
                        error: action.error,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(ProfileActionTypes.UPDATE_PAYMENT_CARD_LIMITS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    state: {
                      ...profileCard.state,
                      totalLimitCard: {
                        state: {
                          ...action.cardLimits,
                        },
                        isLoading: false,
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Create Card reducers
   */
  .handleType(ProfileActionTypes.CREATE_CARD, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: {} }),
  )

  .handleType(ProfileActionTypes.CREATE_CARD_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: {} }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.CREATE_CARD_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: {} }),
  )

  /**
   * Update Card reducers
   */
  .handleType(ProfileActionTypes.UPDATE_CARD, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId } }),
  )

  .handleType(ProfileActionTypes.UPDATE_CARD_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId } }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.UPDATE_CARD_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.card.userId,
      {
        cards: { cardId: action.card.cardId },
      },
      { state: action.card, error: undefined },
    ),
  )

  /**
   * Update Card options reducers
   */
  .handleType(ProfileActionTypes.UPDATE_CARD_OPTIONS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, option: 'option' } }),
  )

  .handleType(ProfileActionTypes.UPDATE_CARD_OPTIONS_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      {
        cards: { cardId: action.cardId, option: 'option' },
      },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.UPDATE_CARD_OPTIONS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.card.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.card.cardId) {
                  return {
                    state: {
                      ...profileCard.state,
                      card: action.card,
                      options: {
                        state: { ...(profileCard.state?.options?.state || {}) },
                        isLoading: false,
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Update Card actions reducers
   */
  .handleType(ProfileActionTypes.UPDATE_CARD_ACTIONS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, action: action.action },
    }),
  )

  .handleType(ProfileActionTypes.UPDATE_CARD_ACTIONS_SETPIN, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    state: {
                      ...profileCard.state,
                      actions: {
                        ...(profileCard.state?.actions || {}),
                        [action.action]: {
                          ...(profileCard.state?.actions?.[action.action] || {}),
                          state: { shouldShowForm: action.hasPayload },
                        },
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(ProfileActionTypes.UPDATE_CARD_ACTIONS_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      {
        cards: { cardId: action.cardId, action: action.modifiedAction },
      },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.UPDATE_CARD_ACTIONS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.card.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.card.cardId) {
                  return {
                    state: {
                      ...profileCard.state,
                      card: action.card,
                      actions: {
                        ...(profileCard.state?.actions || {}),
                        [action.action]: {
                          isLoading: false,
                          error: undefined,
                          state: { shouldShowForm: false },
                        },
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Get Custom Restrictions Group reducers
   */
  .handleType(ProfileActionTypes.GET_CUSTOM_RESTRICTIONS_GROUP, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType },
    }),
  )

  .handleType(ProfileActionTypes.GET_CUSTOM_RESTRICTIONS_GROUP_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.GET_CUSTOM_RESTRICTIONS_GROUP_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType } },
      {
        state: {
          restrictionsGroup: action.restrictionsGroup,
        },
        error: undefined,
      },
    ),
  )

  /**
   * Add Custom Restrictions Group reducers
   */
  .handleType(ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType },
    }),
  )

  .handleType(ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.ADD_CUSTOM_RESTRICTIONS_GROUP_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroup.type } },
      {
        state: {
          restrictionsGroup: action.restrictionsGroup,
        },
        error: undefined,
      },
    ),
  )

  /**
   * Update Custom Restrictions Group reducers
   */
  .handleType(ProfileActionTypes.UPDATE_CUSTOM_RESTRICTIONS_GROUP, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType },
    }),
  )

  .handleType(ProfileActionTypes.UPDATE_CUSTOM_RESTRICTIONS_GROUP_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.UPDATE_CUSTOM_RESTRICTIONS_GROUP_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroup.type } },
      {
        state: {
          restrictionsGroup: action.restrictionsGroup,
        },
        error: undefined,
      },
    ),
  )

  /**
   * Delete Custom Restrictions Group reducers
   */
  .handleType(ProfileActionTypes.DELETE_CUSTOM_RESTRICTIONS_GROUP, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType },
    }),
  )

  .handleType(ProfileActionTypes.DELETE_CUSTOM_RESTRICTIONS_GROUP_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.DELETE_CUSTOM_RESTRICTIONS_GROUP_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, restrictionsGroupType: action.restrictionsGroupType } },
      { state: { card: action.card, deletedRestrictionsGroup: true }, error: undefined },
    ),
  )

  /**
   * Set Current Restrictions Group reducer
   */
  .handleType(ProfileActionTypes.SET_CURRENT_RESTRICTIONS_GROUP, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard: IState<IProfileCard>) => {
                if (profileCard.state?.card.cardId === action.cardId)
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard?.state,
                      restrictionsGroups: {
                        ...(profileCard.state?.restrictionsGroups || ({} as TCardRestrictionsGroups)),
                        currentRestrictionsGroup: action.restrictionsGroupType,
                      },
                    },
                  };

                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /*
   * Get digitized cards reducer
   */

  .handleType(ProfileActionTypes.GET_DIGITIZED_CARDS, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, digitizedCards: {} } }),
  )

  .handleType(ProfileActionTypes.GET_DIGITIZED_CARDS_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, digitizedCards: {} } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.GET_DIGITIZED_CARDS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      digitizedCards: {
                        isLoading: false,
                        state: [...(action.cards ?? [])],
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * Action on digitized cards reducer
   */

  .handleType(ProfileActionTypes.UPDATE_DIGITIZED_CARDS_ACTIONS_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      digitizedCards: {
                        ...profileCard.state.digitizedCards,
                        state: profileCard.state.digitizedCards.state?.map((digitizedCard) => {
                          if (digitizedCard.id === action.digitizedCardId) {
                            return {
                              ...digitizedCard,
                              status: action.status,
                            };
                          }
                          return digitizedCard;
                        }),
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /*
   * Get card enrollment
   */

  .handleType(ProfileActionTypes.GET_CARD_ENROLLMENT, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, enrollments: {} } }),
  )

  .handleType(ProfileActionTypes.GET_CARD_ENROLLMENT_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, enrollments: {} } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.GET_CARD_ENROLLMENT_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      enrollments: {
                        isLoading: false,
                        state: [...action.enrollments],
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /*
   * Enroll card
   */

  .handleType(ProfileActionTypes.ENROLL_CARD, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, enrollments: {} } }),
  )

  .handleType(ProfileActionTypes.ENROLL_CARD_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, enrollments: {} } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.ENROLL_CARD_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      enrollments: {
                        isLoading: false,
                        state: [...(profileCard.state.enrollments.state ?? []), action.method],
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /*
   * Delete card enrollment
   */

  .handleType(ProfileActionTypes.DELETE_OOB_CARD_ENROLLMENT, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { cards: { cardId: action.cardId, enrollments: {} } }),
  )

  .handleType(ProfileActionTypes.DELETE_OOB_CARD_ENROLLMENT_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { cards: { cardId: action.cardId, enrollments: {} } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.DELETE_OOB_CARD_ENROLLMENT_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId === action.userId) {
        return {
          ...profile,
          state: {
            ...profile.state,
            profileCards: {
              ...profile.state?.profileCards,
              state: profile.state?.profileCards?.state?.map((profileCard) => {
                if (profileCard.state && profileCard.state?.card.cardId === action.cardId) {
                  return {
                    ...profileCard,
                    state: {
                      ...profileCard.state,
                      enrollments: {
                        isLoading: false,
                        state: [
                          ...(profileCard.state.enrollments.state ?? []).filter((e) => e.id !== action.authMethodId),
                        ],
                        error: undefined,
                      },
                    },
                  };
                }
                return profileCard;
              }),
            },
          },
        };
      }

      return profile;
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * GET wallet SCA reducers
   */
  .handleType(ProfileActionTypes.GET_SCA, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { sca: { wallets: {} } }),
  )

  .handleType(ProfileActionTypes.GET_SCA_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { sca: { wallets: {} } }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.GET_SCA_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { wallets: {} } },
      { state: action.scaWallets, error: undefined },
    ),
  )

  /**
   * GET deleted SCA wallets reducers
   */
  .handleType(ProfileActionTypes.GET_DELETED_SCA, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { sca: { deletedWallets: {} } }),
  )

  .handleType(ProfileActionTypes.GET_DELETED_SCA_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { sca: { deletedWallets: {} } }, { error: action.error }),
  )

  .handleType(ProfileActionTypes.GET_DELETED_SCA_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { deletedWallets: {} } },
      { state: action.scaWallets, error: undefined },
    ),
  )

  /**
   * Delete wallet SCA reducers
   */
  .handleType(ProfileActionTypes.DELETE_SCA, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      sca: { scaWalletId: action.scaWalletId },
    }),
  )

  .handleType(ProfileActionTypes.DELETE_SCA_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWalletId } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.DELETE_SCA_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId !== action.userId) return profile;

      return {
        ...profile,
        state: {
          ...profile.state,
          scaWallets: {
            ...profile.state?.scaWallets,
            state: profile.state?.scaWallets?.state?.filter(
              (scaState) => scaState.state?.scaWalletId !== action.scaWalletId,
            ),
          },
        },
      };
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  /**
   * RESET PIN wallet SCA reducers
   */
  .handleType(ProfileActionTypes.RESET_PIN_SCA, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      sca: { scaWalletId: action.scaWalletId },
    }),
  )

  .handleType(ProfileActionTypes.RESET_PIN_SCA_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWalletId } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.RESET_PIN_SCA_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWallet.scaWalletId } },
      { state: action.scaWallet, error: undefined },
    ),
  )

  /**
   *  Unlock SCA reducers
   */
  .handleType(ProfileActionTypes.UNLOCK_SCA, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { sca: { scaWalletId: action.scaWalletId } }),
  )

  .handleType(ProfileActionTypes.UNLOCK_SCA_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWalletId } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.UNLOCK_SCA_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWallet.scaWalletId } },
      { state: action.scaWallet, error: undefined },
    ),
  )

  /**
   *  Lock SCA reducers
   */
  .handleType(ProfileActionTypes.LOCK_SCA, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { sca: { scaWalletId: action.scaWalletId } }),
  )

  .handleType(ProfileActionTypes.LOCK_SCA_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWalletId } },
      { error: action.error },
    ),
  )

  .handleType(ProfileActionTypes.LOCK_SCA_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { sca: { scaWalletId: action.scaWallet.scaWalletId } },
      { state: action.scaWallet, error: undefined },
    ),
  )

  /**
   * GET Tax Residence
   */
  .handleType(ProfileActionTypes.GET_TAX_RESIDENCE, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { taxResidences: { items: {} } }),
  )

  .handleType(ProfileActionTypes.GET_TAX_RESIDENCE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { items: {} } },
      { state: action.taxResidences, error: undefined },
    ),
  )

  .handleType(ProfileActionTypes.GET_TAX_RESIDENCE_FAIL, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { taxResidences: { items: {} } }, { error: action.error }),
  )

  /**
   * Add Tax Residence
   */
  .handleType(taxResidenceTypes.ADD_TAX_RESIDENCE, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { create: {} } },
      {
        state: action?.batchIndex
          ? {
              [action.batchIndex]: { isLoading: true },
            }
          : undefined,
      },
    ),
  )

  .handleType(taxResidenceTypes.ADD_TAX_RESIDENCE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { create: {} } },
      {
        state: {
          taxResidence: action.taxResidence,
          ...(action?.batchIndex
            ? {
                [action.batchIndex]: { isLoading: false, error: undefined },
              }
            : undefined),
        },
        error: undefined,
      },
    ),
  )

  .handleType(taxResidenceTypes.ADD_TAX_RESIDENCE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { create: {} } },
      {
        error: action.error,
        state: action?.batchIndex
          ? {
              [action.batchIndex]: { isLoading: false, error: action.error },
            }
          : undefined,
      },
    ),
  )

  /**
   *  Update tax residence reducers
   */
  .handleType(taxResidenceTypes.UPDATE_TAX_RESIDENCE, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, { taxResidences: { id: action.taxResidenceId } }),
  )

  .handleType(taxResidenceTypes.UPDATE_TAX_RESIDENCE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { id: action.taxResidence.id } },
      { state: action.taxResidence, error: undefined },
    ),
  )

  .handleType(taxResidenceTypes.UPDATE_TAX_RESIDENCE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { id: action.taxResidenceId } },
      { error: action.error },
    ),
  )

  /**
   * Delete tax residence reducers
   */
  .handleType(taxResidenceTypes.DELETE_TAX_RESIDENCE, (state, action) =>
    handleAsyncProfileReducer(state, action.userId, {
      taxResidences: { id: action.taxResidenceId },
    }),
  )

  .handleType(taxResidenceTypes.DELETE_TAX_RESIDENCE_SUCCESS, (state, action) => {
    const profiles = state.profiles.state?.map((profile): IState<IProfile> => {
      if (profile.state?.user.state?.userId !== action.userId) return profile;

      return {
        ...profile,
        state: {
          ...profile.state,
          taxResidences: {
            ...profile.state?.taxResidences,
            state: profile.state?.taxResidences?.state?.filter(
              (taxResidence) => taxResidence.state?.id !== action.taxResidenceId,
            ),
          },
        },
      };
    });

    return {
      ...state,
      profiles: {
        state: profiles,
      },
    };
  })

  .handleType(taxResidenceTypes.DELETE_TAX_RESIDENCE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.userId,
      { taxResidences: { id: action.taxResidenceId } },
      { error: action.error },
    ),
  )

  /**
   * Add Child Tax Residence
   */
  .handleType(taxResidenceTypes.ADD_CHILD_TAX_RESIDENCE, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.parentId,
      { children: { items: { id: action.userId, taxResidences: {} } } },
      {
        state: action?.batchIndex
          ? {
              [action.batchIndex]: { isLoading: true },
            }
          : undefined,
      },
    ),
  )
  .handleType(taxResidenceTypes.ADD_CHILD_TAX_RESIDENCE_SUCCESS, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.parentId,
      { children: { items: { id: action.userId, taxResidences: {} } } },
      {
        state: {
          taxResidence: action.taxResidence,
          ...(action?.batchIndex
            ? {
                [action.batchIndex]: { isLoading: false, error: undefined },
              }
            : undefined),
        },
        error: undefined,
      },
    ),
  )

  .handleType(taxResidenceTypes.ADD_CHILD_TAX_RESIDENCE_FAIL, (state, action) =>
    handleAsyncProfileReducer(
      state,
      action.parentId,
      { children: { items: { id: action.userId, taxResidences: {} } } },
      {
        error: action.error,
        state: action?.batchIndex
          ? {
              [action.batchIndex]: { isLoading: false, error: action.error },
            }
          : undefined,
      },
    ),
  );

export const profileReducer = createReducer<IProfileState, TProfileAction | TTaxResidenceGroupAction>(initialState, {
  ...baseProfileReducer.handlers,
  ...profileKycReducer(handleAsyncProfileReducer).handlers,
  ...companyMemberReducer(handleAsyncProfileReducer).handlers,
  ...walletReducer.handlers,
  ...userReducer.handlers,
});
